import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { isOnboardingGateDisabled, OnboardingStatus, useOnboarding } from '@/api/onboarding/onboarding';
import { useGetWorkspacesByTenant } from '@/api/workspaces/useGetWorkspacesByTenant';
import { useAppDispatch } from '@/app/hooks';
import ClerkOrgGate from '@/component-library/widgets/ClerkOrgGate/ClerkOrgGate';
import { useLinkPendingClerkTenant } from '@/hooks/useLinkPendingClerkTenant';
import { useReloadClerkUserAfterServerProfileSync } from '@/hooks/useReloadClerkUserAfterServerProfileSync';
import DashboardRoutesWrapper from '@/modules/Dashboard/DashboardRoutesWrapper/DashboardRoutesWrapper';
import { setSelectedWorkspace } from '@/modules/Dashboard/Workspaces/workspaceSlice';
import { isClerkAuthEnabled } from '@/utils/auth/authMode';
import protectedRoute from '@/utils/routes/PrivateRoute';
import { AppRoutes } from '@/utils/routes/router';

import LoggedInHeader from '../LoggedInHeader/LoggedInHeader';
import SideMenu from '../SideMenu/SideMenu';
import { setCollapsedSidebar } from '../SideMenu/sideMenuSlice';

// Cache key for onboarding status
const ONBOARDING_CACHE_KEY = 'onboarding_status_cache';
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes for incomplete onboarding

interface CachedOnboardingStatus {
  status: OnboardingStatus;
  timestamp: number;
}

type ProtectedLayoutCoreProps = {
  children: ReactNode;
  isLinking: boolean;
  clerkLinkComplete: boolean;
  linkError: string | null;
};

// Helper functions for caching
const getCachedOnboardingStatus = (): CachedOnboardingStatus | null => {
  try {
    const cached = sessionStorage.getItem(ONBOARDING_CACHE_KEY);
    if (!cached) return null;
    const parsed: CachedOnboardingStatus = JSON.parse(cached);

    // If onboarding is completed, cache it forever (for the session)
    // Once completed, there's no going back
    if (parsed.status.isCompleted) {
      return parsed;
    }

    // For incomplete onboarding, check if cache is still valid
    const now = Date.now();
    if (now - parsed.timestamp < CACHE_DURATION_MS) {
      return parsed;
    }

    // Cache expired for incomplete onboarding, remove it
    sessionStorage.removeItem(ONBOARDING_CACHE_KEY);
    return null;
  } catch {
    return null;
  }
};

const setCachedOnboardingStatus = (status: OnboardingStatus): void => {
  try {
    const cached: CachedOnboardingStatus = {
      status,
      timestamp: Date.now(),
    };
    sessionStorage.setItem(ONBOARDING_CACHE_KEY, JSON.stringify(cached));
  } catch {
    // Ignore storage errors
  }
};

export const clearOnboardingCache = (): void => {
  try {
    sessionStorage.removeItem(ONBOARDING_CACHE_KEY);
  } catch {
    // Ignore storage errors
  }
};

const ProtectedLayoutCore = ({
  children,
  isLinking,
  clerkLinkComplete,
  linkError,
}: ProtectedLayoutCoreProps) => {
  const dispatch = useAppDispatch();
  useGetWorkspacesByTenant({ enabled: clerkLinkComplete });
  const navigate = useNavigate();
  const location = useLocation();
  const onboarding = useOnboarding();
  const [isCheckingOnboarding, setIsCheckingOnboarding] = useState(() => !isOnboardingGateDisabled);
  const hasCheckedOnboardingRef = useRef<boolean>(false);
  const onboardingCompletedRef = useRef<boolean>(false);

  useEffect(() => {
    const workspaceFromSessionStorage = sessionStorage.getItem('workspace');
    const sideBarStateFromSessionStorage = sessionStorage.getItem('sideBarState');
    workspaceFromSessionStorage && dispatch(setSelectedWorkspace(JSON.parse(workspaceFromSessionStorage)));
    sideBarStateFromSessionStorage && dispatch(setCollapsedSidebar(JSON.parse(sideBarStateFromSessionStorage)));
  }, [dispatch]);

  // Check onboarding status and redirect if not completed
  useEffect(() => {
    if (isOnboardingGateDisabled) {
      return;
    }
    // Skip check if already on onboarding page
    if (location.pathname === AppRoutes.onboardingForm) {
      setIsCheckingOnboarding(false);
      hasCheckedOnboardingRef.current = false; // Reset when navigating to onboarding
      onboardingCompletedRef.current = false;
      return;
    }

    // If we already know onboarding is completed, skip the check
    if (onboardingCompletedRef.current) {
      setIsCheckingOnboarding(false);
      return;
    }

    // Check cache first
    const cached = getCachedOnboardingStatus();
    if (cached && cached.status.isCompleted) {
      onboardingCompletedRef.current = true;
      setIsCheckingOnboarding(false);
      return;
    }

    // Only check once per session, not on every route change
    if (hasCheckedOnboardingRef.current) {
      setIsCheckingOnboarding(false);
      return;
    }

    let isMounted = true;
    let checkTimeout: NodeJS.Timeout | null = null;

    const checkOnboardingStatus = async () => {
      try {
        if (isMounted) {
          setIsCheckingOnboarding(true);
          hasCheckedOnboardingRef.current = true;
        }

        const status = await onboarding.checkStatus();

        if (!isMounted) return;

        // Cache the status
        setCachedOnboardingStatus(status);

        // If onboarding is not completed, redirect to onboarding page
        if (!status.isCompleted) {
          onboardingCompletedRef.current = false;
          navigate(AppRoutes.onboardingForm, { replace: true });
          return;
        }

        // Onboarding is completed
        onboardingCompletedRef.current = true;
      } catch (error) {
        // Don't redirect on errors - only redirect if status explicitly says not completed
        // This prevents false redirects due to network issues
        console.error('Failed to check onboarding status:', error);

        // If we have a cached status, use it
        const cached = getCachedOnboardingStatus();
        if (cached) {
          if (!cached.status.isCompleted) {
            navigate(AppRoutes.onboardingForm, { replace: true });
          } else {
            onboardingCompletedRef.current = true;
          }
        } else {
          // No cache and error - assume completed to avoid false redirects
          // The user can still access the app, and if they're not onboarded,
          // they'll be redirected when they try to use features
          onboardingCompletedRef.current = true;
        }
      } finally {
        if (isMounted) {
          setIsCheckingOnboarding(false);
        }
      }
    };

    // Debounce the check to prevent rapid-fire requests
    checkTimeout = setTimeout(() => {
      checkOnboardingStatus();
    }, 100);

    return () => {
      isMounted = false;
      if (checkTimeout) {
        clearTimeout(checkTimeout);
      }
    };
  }, [location.pathname, navigate]); // Only check when pathname changes

  // Show loading state while checking onboarding
  if (isLinking) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-custom-blue"></div>
          <p className="mt-4 text-custom-darkSilver">Linking your organization…</p>
        </div>
      </div>
    );
  }

  if (!clerkLinkComplete && linkError) {
    return (
      <div className="flex items-center justify-center h-full px-6">
        <div className="max-w-md text-center">
          <p className="text-custom-red text-sm font-medium">{linkError}</p>
          <p className="mt-3 text-sm text-custom-darkSilver">
            Refresh the page to retry. If the company is already linked in the database, remove{' '}
            <code className="text-xs">churnsignal_pending_tenant_id</code> from browser storage and refresh again.
          </p>
        </div>
      </div>
    );
  }

  if (isCheckingOnboarding) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-custom-blue"></div>
          <p className="mt-4 text-custom-darkSilver">Checking onboarding status...</p>
        </div>
      </div>
    );
  }

  return (
    <ClerkOrgGate>
      <div className="flex h-full min-h-0 min-w-0">
        <SideMenu />
        <DashboardRoutesWrapper>
          <LoggedInHeader>{children}</LoggedInHeader>
        </DashboardRoutesWrapper>
      </div>
    </ClerkOrgGate>
  );
};

const ClerkProtectedLayout = ({ children }: { children: ReactNode }) => {
  const { isLinking, isLinkComplete: clerkLinkComplete, linkError } = useLinkPendingClerkTenant();
  useReloadClerkUserAfterServerProfileSync(clerkLinkComplete && !isLinking);

  return (
    <ProtectedLayoutCore isLinking={isLinking} clerkLinkComplete={clerkLinkComplete} linkError={linkError}>
      {children}
    </ProtectedLayoutCore>
  );
};

const ProtectedLayout = ({ children }: { children: ReactNode }) => {
  if (isClerkAuthEnabled()) {
    return <ClerkProtectedLayout>{children}</ClerkProtectedLayout>;
  }

  return (
    <ProtectedLayoutCore isLinking={false} clerkLinkComplete linkError={null}>
      {children}
    </ProtectedLayoutCore>
  );
};

export default protectedRoute(ProtectedLayout);
